import { CheckIcon } from 'lucide-react';
import { memo, useCallback } from 'react';

import { Button, type ButtonProps } from '@air/primitive-button';
import { DropdownMenu, DropdownMenuItem, DropdownMenuLabel } from '@air/primitive-dropdown-menu';

import { PLAYBACK_RATES } from '../constants/playbackRate';
import { usePlayerStore } from './PlayerContextProvider';

export type PlayerPlaybackRateButtonProps = Omit<ButtonProps, 'children' | 'label'>;

export const PlayerPlaybackRateButton = memo((props: PlayerPlaybackRateButtonProps) => {
  const playbackRate = usePlayerStore((state) => state.playbackRate);
  const videoElement = usePlayerStore((state) => state.videoElement);

  const isDefaultPlaybackRate = playbackRate === 1;

  const handlePlaybackRateChange = useCallback(
    (playbackRate: number) => {
      if (videoElement) {
        videoElement.playbackRate = playbackRate;
      }
    },
    [videoElement],
  );

  return (
    <DropdownMenu
      side="top"
      trigger={
        <Button
          appearance="ghost"
          color={isDefaultPlaybackRate ? 'grey' : 'blue'}
          data-testid="PLAYER_PLAYBACK_RATE_BUTTON"
          {...props}
        >
          {playbackRate}×
        </Button>
      }
    >
      <DropdownMenuLabel>Speed</DropdownMenuLabel>
      {PLAYBACK_RATES.map((speed) => (
        <DropdownMenuItem
          key={speed}
          label={`${speed}×`}
          onSelect={() => handlePlaybackRateChange(speed)}
          suffix={speed === playbackRate ? <CheckIcon className="size-4" /> : null}
        />
      ))}
    </DropdownMenu>
  );
});

PlayerPlaybackRateButton.displayName = 'PlayerPlaybackRateButton';
